import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Megaphone } from 'lucide-react';

import { UserContext } from '../../context/UserContext';
import ReportComposer from './ReportComposer';

/**
 * FAB «Reportar» (Smack F1, lo robado de Jam): visible en cualquier pantalla
 * para owner/staff/superadmin de un venue con Smack activo. Captura el
 * pantallazo ANTES de abrir el overlay (para no salir en la foto) con
 * html2canvas — DOM a canvas, sin permisos del navegador, funciona en tablets.
 * Caveat asumido: imágenes S3 cross-origin pueden salir en blanco.
 *
 * VA ABAJO Y CENTRADO, y eso no es estético. Nació abajo a la DERECHA y esa
 * esquina es de la app: con `z-[100010]` se pintaba encima del pie del carrito
 * de Sala, del pie del panel de Carritos, de la barra `.df-bottombar` y del
 * cajón `.df-cart` del dispensario nuevo (z-71), de la burbuja del Asistente IA
 * (z-50) y de los toasts del POS (z-50) — reporte 29-08-2026. Ningún z-index
 * arregla eso: el problema es posicional.
 *
 * Por qué el centro y no la esquina izquierda, que fue el primer intento: las
 * barras inferiores de esta app reparten su contenido en los BORDES —el total y
 * el recuento a la izquierda, «Ver carrito» / «Validar compra» a la derecha— y
 * dejan el medio vacío. Medido con `elementsFromPoint` sobre el POS: a la
 * izquierda el FAB se sentaba sobre «Total: €X» (información del carrito, o sea
 * el mismo pecado con otro nombre) y más abajo sobre la etiqueta «Historial del
 * socio»; más arriba están las tarjetas de producto y su «Añadir». El único
 * punto donde no hay nada debajo es el centro de la barra.
 *
 * El `z-[100010]` y el portal al body SE QUEDAN, y son el motivo de que no baste
 * con moverlo a la cabecera: reportar tiene que funcionar TAMBIÉN con un modal
 * abierto —«no me deja sacar a este socio» ocurre dentro de un modal y la
 * captura debe incluirlo— y los modales son `fixed inset-0` con telón, así que
 * cualquier disparador que viva en el cromo queda debajo. Montado en el árbol
 * del Layout sin portal, cualquier contexto de apilamiento intermedio lo dejaba
 * por debajo y el telón se comía el clic.
 *
 * Por debajo de `md` el sidebar es un cajón `w-64` y, con él abierto, el FAB cae
 * encima de sus enlaces del pie (logout incluido): por eso `Layout` le pasa
 * `oculto` mientras está desplegado. Se resuelve con `hidden md:flex` y NO
 * desmontando —ver el comentario del propio botón—.
 *
 * Candados: `src/tests/wave6/ReportarNoTapaElCarrito.wave6.test.jsx` y, con
 * geometría de navegador, `tests/e2e/cart-tablet-landscape.spec.js`.
 * La excepción medida, por si alguien la busca: el cajón del carrito del
 * dispensario VIEJO (`Dispenser.jsx`) va dentro de un portal con
 * `zIndex: 2147483647`, así que ese se pintaba por encima del FAB y nunca fue
 * víctima.
 */
/**
 * La puerta de visibilidad del FAB, compartida con `Layout`: el mismo cálculo
 * decide si se pinta el botón y si `main` reserva sitio para él. Si viviera
 * solo aquí, el Layout tendría que adivinar cuándo hay FAB, y las dos verdades
 * se separarían con el primer cambio de rol o de flag.
 */
export function useReportarVisible() {
  const location = useLocation();
  const userCtx = useContext(UserContext);
  const [token, , , venueID, , , , accessPrivileges] = userCtx;
  // Posición 18 (candado ComanderoMenuSinRecargar): flag de Smack del venue.
  const venueSmackEnabled = Boolean(userCtx[18]);
  const rolPermitido = ['owner', 'staff', 'superadmin'].includes(accessPrivileges);
  const enSmack = location.pathname.startsWith('/smack');
  // El reporte se publica en el canal del club: sin venue con flag no hay destino.
  return Boolean(token && venueID && venueSmackEnabled && rolPermitido && !enSmack);
}

/**
 * El hueco que el FAB necesita al pie del contenido, por tamaño. Es el mismo
 * `bottom-*` del botón más su alto (40 px) y un aire de 8 px: con esto `main`
 * se puede desplazar lo justo para que la última fila de acciones de una
 * pantalla —«Salir» en la tarjeta de Entradas, «Ver perfil» en Socios— suba
 * por encima del botón. Sin hueco, en un móvil de 390 la tarjeta cabía entera
 * y no había scroll: «Salir» se quedaba debajo del FAB sin forma de destaparlo
 * (auditoría de UI 12-09-2026, UI-001). Flotante se queda: captura la
 * pantalla entera y tiene que funcionar con un modal abierto. El `lg:` hace
 * falta: `main` lleva `lg:p-8`, que Tailwind ordena después de `md:` y pisaría
 * el `md:pb-20` en la tablet apaisada.
 */
export const HUECO_REPORTAR = 'pb-44 sm:pb-32 md:pb-20 lg:pb-20';

export default function ReportButton({ oculto = false }) {
  const location = useLocation();
  const visible = useReportarVisible();

  const [abierto, setAbierto] = useState(false);
  const [captura, setCaptura] = useState(null); // dataURL o null
  const [capturando, setCapturando] = useState(false);

  if (!visible) return null;

  const capturarPantalla = async () => {
    // Con la fuente (Inter) a medio cargar, cualquier captura mide con la
    // fallback y recorta los textos truncados: esperar a que estén listas.
    try {
      await document.fonts.ready;
    } catch {
      /* API ausente en navegadores viejos: se captura igual */
    }
    const escala = Math.min(window.devicePixelRatio || 1, 2);
    const sinOverlaysPropios = (el) => el?.getAttribute?.('data-smack-report') !== 'true';

    // 1º modern-screenshot (foreignObject: el TEXTO lo pinta el propio motor
    // del navegador — html2canvas lo dibuja él mismo y con Inter + `truncate`
    // corta letras a la mitad, visto en el smoke del 22-ago). Import dinámico:
    // ninguna de las dos librerías entra en el bundle principal.
    try {
      const { domToPng } = await import('modern-screenshot');
      const dataUrl = await domToPng(document.body, {
        scale: escala,
        filter: sinOverlaysPropios,
      });
      // Un PNG en blanco comprime a casi nada: por debajo de este umbral se
      // considera captura fallida y se cae al respaldo.
      if (dataUrl && dataUrl.length > 10_000) return dataUrl;
    } catch {
      /* cae al respaldo */
    }
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: escala,
        ignoreElements: (el) => el.getAttribute?.('data-smack-report') === 'true',
      });
      return canvas.toDataURL('image/png');
    } catch {
      // Sin captura no se bloquea el reporte: el texto sigue valiendo.
      return null;
    }
  };

  const abrirConCaptura = async () => {
    if (capturando) return;
    setCapturando(true);
    const dataUrl = await capturarPantalla();
    setCaptura(dataUrl);
    setCapturando(false);
    setAbierto(true);
  };

  return createPortal(
    <>
      {/* Con el composer abierto el disparador NO se pinta: quedaba visible a
          través del telón y, sin trampa de foco en el diálogo, Shift+Tab volvía
          a él y Enter lanzaba una segunda captura que se tiraba. */}
      {!abierto && (
        <button
          type="button"
          data-smack-report="true"
          onClick={abrirConCaptura}
          disabled={capturando}
          // #FF5400: acento de marca de Smack (decisión 22-ago: se queda) — fuera de tokens.css a propósito: el design system es slate y este naranja es exclusivo del módulo.
          // `oculto` se resuelve con `hidden md:flex`, y NO desmontando ni entrando
          // en la puerta de visibilidad, por dos motivos: (a) `sidebarOpen` solo lo
          // cierran botones `md:hidden`, así que al girar la tablet a apaisado el
          // FAB no volvería nunca; (b) desmontar a mitad de la captura —que tarda
          // segundos— mata el componente y el reporte se pierde sin decir nada.
          className={`fixed bottom-32 left-1/2 z-[100010] -translate-x-1/2 items-center gap-2 rounded-full bg-[#FF5400] px-4 py-2.5 text-sm font-medium text-white shadow-lg hover:opacity-90 disabled:opacity-60 sm:bottom-20 md:bottom-6 ${
            oculto ? 'hidden md:flex' : 'flex'
          }`}
          aria-label="Reportar un problema"
        >
          <Megaphone size={16} />
          {/* Sin etiqueta por debajo de `sm`, y por encima de la barra: medido en
              un móvil de 390 con 4 líneas, el hueco entre el total y el «Ver
              carrito» es de 8 px — la barra va llena de borde a borde y ahí abajo
              no cabe nada. De 640 en adelante el hueco pasa de 200 px. */}
          <span className="hidden sm:inline">{capturando ? 'Capturando…' : 'Reportar'}</span>
        </button>
      )}

      {abierto && (
        <ReportComposer
          captura={captura}
          ruta={location.pathname}
          onCerrar={() => {
            setAbierto(false);
            setCaptura(null);
          }}
        />
      )}
    </>,
    document.body
  );
}
